import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  QrCode2,
  Code,
  Delete,
  Link as LinkIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import {
  getPaymentLinks,
  createPaymentLink,
  deactivatePaymentLink,
  getPartnerProducts,
} from '../../services/api/partnerService';
import { PaymentLinkListResponse, PaymentLink, PartnerProduct } from '../../types';

type PaymentLinkItem = PaymentLink;

const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PartnerPaymentLinksPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<PaymentLinkListResponse | null>(null);
  const [products, setProducts] = useState<PartnerProduct[]>([]);

  // 作成ダイアログ
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number>(0);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string>('');
  const [creating, setCreating] = useState(false);

  // 詳細ダイアログ
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedLink, setSelectedLink] = useState<PaymentLinkItem | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  // 通知
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [linksData, productsData] = await Promise.all([
        getPaymentLinks(),
        getPartnerProducts(),
      ]);
      setLinks(linksData);
      setProducts(productsData.items.filter((p) => p.is_enabled));
    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!selectedProductId || !selectedPlanCode) return;

    setCreating(true);
    try {
      await createPaymentLink(selectedProductId, selectedPlanCode);
      setCreateDialog(false);
      setSelectedProductId(0);
      setSelectedPlanCode('');
      await loadData();
      setSnackbar('決済リンクを作成しました');
    } catch (err: any) {
      console.error('Create error:', err);
      setError(err.response?.data?.detail || '決済リンクの作成に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (linkId: number) => {
    if (!confirm('この決済リンクを無効化しますか？')) return;

    try {
      await deactivatePaymentLink(linkId);
      await loadData();
      setSnackbar('決済リンクを無効化しました');
    } catch (err: any) {
      console.error('Deactivate error:', err);
      setError(err.response?.data?.detail || '無効化に失敗しました');
    }
  };

  const handleOpenDetail = async (link: PaymentLinkItem) => {
    setSelectedLink(link);
    setDetailTab(0);

    // QRコード生成
    try {
      const qr = await QRCode.toDataURL(link.url, { width: 200, margin: 2 });
      setQrCodeUrl(qr);
    } catch (err) {
      console.error('QR generation error:', err);
    }

    setDetailDialog(true);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar(`${label}をコピーしました`);
  };

  const selectedProduct = products.find((p) => p.id === selectedProductId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            決済リンク
          </Typography>
          <Typography variant="body2" color="text.secondary">
            顧客に共有する決済リンクを作成・管理
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
          disabled={products.length === 0}
        >
          新規作成
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {products.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          決済リンクを作成するには、まず商品を有効化してください。
        </Alert>
      )}

      {/* リンク一覧 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>商品・プラン</TableCell>
              <TableCell>価格</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="center">クリック数</TableCell>
              <TableCell align="center">購入数</TableCell>
              <TableCell>作成日</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links?.items.map((link) => (
              <TableRow key={link.id}>
                <TableCell>
                  <Typography fontWeight="bold">{link.product_name}</Typography>
                  <Chip label={link.plan_code} size="small" />
                </TableCell>
                <TableCell>
                  {link.price === 0 ? (
                    <Chip label="無料" color="success" size="small" />
                  ) : (
                    formatCurrency(link.price)
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {link.url}
                    </Typography>
                    <Tooltip title="コピー">
                      <IconButton size="small" onClick={() => handleCopy(link.url, 'URL')}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">{link.click_count}</TableCell>
                <TableCell align="center">{link.purchase_count}</TableCell>
                <TableCell>{formatDate(link.created_at)}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="詳細">
                      <IconButton onClick={() => handleOpenDetail(link)}>
                        <QrCode2 />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="無効化">
                      <IconButton
                        color="error"
                        onClick={() => handleDeactivate(link.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {(!links || links.items.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    決済リンクがありません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 作成ダイアログ */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>決済リンクを作成</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>商品</InputLabel>
            <Select
              value={selectedProductId}
              label="商品"
              onChange={(e) => {
                setSelectedProductId(e.target.value as number);
                setSelectedPlanCode('');
              }}
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProduct && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>プラン</InputLabel>
              <Select
                value={selectedPlanCode}
                label="プラン"
                onChange={(e) => setSelectedPlanCode(e.target.value)}
              >
                {selectedProduct.plans.map((plan) => {
                  const customPrice = selectedProduct.custom_pricing?.[plan.code]?.price;
                  const price = customPrice ?? selectedProduct.base_prices[plan.code] ?? 0;
                  return (
                    <MenuItem key={plan.code} value={plan.code}>
                      {plan.name} - {price === 0 ? '無料' : formatCurrency(price)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleCreateLink}
            disabled={creating || !selectedProductId || !selectedPlanCode}
          >
            {creating ? <CircularProgress size={20} /> : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 詳細ダイアログ */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedLink?.product_name} - {selectedLink?.plan_code}
        </DialogTitle>
        <DialogContent>
          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<LinkIcon />} label="URL" />
            <Tab icon={<QrCode2 />} label="QRコード" />
            <Tab icon={<Code />} label="埋め込み" />
          </Tabs>

          {detailTab === 0 && (
            <Box>
              <TextField
                fullWidth
                value={selectedLink?.url || ''}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={() => selectedLink && handleCopy(selectedLink.url, 'URL')}
              >
                URLをコピー
              </Button>
            </Box>
          )}

          {detailTab === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              {qrCodeUrl && (
                <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: '100%' }} />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                このQRコードを顧客に共有してください
              </Typography>
            </Box>
          )}

          {detailTab === 2 && (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={selectedLink?.embed_code || ''}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={() => selectedLink && handleCopy(selectedLink.embed_code, '埋め込みコード')}
              >
                埋め込みコードをコピー
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default PartnerPaymentLinksPage;
